import axios from "axios";
import { HTTP_STATUS_CODE } from "constants/constants";
import { getAuthToken } from "api/authToken";

const axiosAPI = axios.create({
  baseURL: process.env.REACT_APP_APP_BACK_URL,
  transformResponse: [
    function (data) {
      // Force proper UTF-8 decoding
      if (typeof data === "string") {
        try {
          return JSON.parse(data);
        } catch (e) {
          return data;
        }
      }
      return data;
    },
  ],
});

// Interceptor to dynamically inject the token on each request
axiosAPI.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Annual balancesheet public requests must be sent without the user's bearer token,
  // so we skip the automatic auth header injection and remove it if it was already set.
  else if (config?.headers?.Authorization && config?.omitAuthHeader) {
    delete config.headers.Authorization;
  }
  return config;
});

// Headers without token (added dynamically by the interceptor)
const headersStd = {
  "Content-Type": "application/json; charset=utf-8",
  "Accept": "application/json; charset=utf-8",
};

const headersFile = {
  "Content-Type": "multipart/form-data",
};

const headerTextPlain = {
  "Content-Type": "text/plain; charset=utf-8",
};

const isAuthRequest = (requestUrl = "") =>
  requestUrl.includes("/auth/sso") || requestUrl.includes("/auth/login");

const ApiRequest = async (
  method,
  url,
  request,
  headers,
  responseType,
  options = {}
) => {
  try {
    const { data } = await axiosAPI({
      method,
      url,
      responseType,
      data: request,
      headers,
      ...options,
    });
    return Promise.resolve(data);
  } catch (err) {
    const { status } = err.response || {};
    const requestUrl = err.config?.url || "";

    if (
      status === HTTP_STATUS_CODE.UNAUTHORIZED &&
      !options?.suppressUnauthorizedRedirect
    ) {
      if (isAuthRequest(requestUrl)) {
        return Promise.reject(err);
      }
      localStorage.clear();
      window.location.href = "/login";
      return;
      //Some requests, like annual balancesheet public upload, must handle 401/restricted errors locally instead of triggering the global redirect.
    } else if (
      status === HTTP_STATUS_CODE.RESTRICTED_ACCESS &&
      !options?.suppressUnauthorizedRedirect &&
      !options?.handleForbiddenLocally
    ) {
      window.location.href = `${window.location.pathname}/restricted-access`;
    }
    return Promise.reject(err);
  }
};

const get = (url, request, options) =>
  (!window.location.pathname.endsWith("/restricted-access") ||
    options?.bypassRestriction) &&
  ApiRequest("get", url, request, headersStd, undefined, options);
// Adding the options parameter to forward request-specific config, and `undefined` to keep it in the correct argument position when no responseType is needed.
const getBlob = (url, request, options) =>
  ApiRequest("get", url, request, headersStd, "blob", options);
const deleteRequest = (url, request, options) =>
  ApiRequest("delete", url, request, headersStd, undefined, options);
const post = (url, request, options) =>
  ApiRequest("post", url, request, headersStd, undefined, options);

const postFile = (url, request, options) =>
  ApiRequest("post", url, request, headersFile, undefined, options);

const putFile = (url, request, options) =>
  ApiRequest("put", url, request, headersFile, undefined, options);

const putTextPlain = (url, request, options) =>
  ApiRequest("put", url, request, headerTextPlain, undefined, options);

const put = (url, request, options) =>
  ApiRequest("put", url, request, headersStd, undefined, options);
const patch = (url, request, options) =>
  ApiRequest("patch", url, request, headersStd, undefined, options);

const patchTextPlain = (url, request, options) =>
  ApiRequest("patch", url, request, headerTextPlain, undefined, options);

const API = {
  patchTextPlain,
  get,
  getBlob,
  delete: deleteRequest,
  post,
  postFile,
  put,
  putFile,
  patch,
  putTextPlain,
};

export default API;
